/**
 * Language Context
 * Provides global language and dialect state management
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { 
  SupportedLanguage, 
  SupportedDialect, 
  TranslationStrings, 
  LanguagePreferences 
} from "./types";
import { getTranslations, getTranslationsAsync } from "./translations";
import { detectDeviceLanguage, getLanguageInfo } from "./languages";
import { 
  getDialectMotivation, 
  getDialectSuccess, 
  getDialectReminder, 
  getDialectWelcome,
  getDialectStreak 
} from "./dialects";

const LANGUAGE_STORAGE_KEY = "hydrolife_language_preferences";
const LANGUAGE_ONBOARDING_KEY = "hydrolife_language_onboarding_complete";

interface LanguageContextValue {
  // Current settings
  language: SupportedLanguage;
  dialect: SupportedDialect | null;
  dialectEnabled: boolean;
  
  // Translation strings
  t: TranslationStrings;
  
  // Direction for RTL support
  dir: "ltr" | "rtl";
  
  // Language management
  setLanguage: (lang: SupportedLanguage) => void;
  setDialect: (dialect: SupportedDialect | null) => void;
  setDialectEnabled: (enabled: boolean) => void;
  
  // Dialect messages (for human-centric moments only)
  getMotivation: () => string;
  getSuccess: () => string;
  getReminder: () => string;
  getWelcome: () => string;
  getStreakMessage: () => string;
  
  // Onboarding state
  hasCompletedLanguageOnboarding: boolean;
  completeLanguageOnboarding: () => void;
  
  // Device language
  deviceLanguage: SupportedLanguage;
  useDeviceLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [preferences, setPreferences] = useState<LanguagePreferences>(() => {
    if (typeof window === "undefined") {
      return { language: "en", dialect: null, dialectEnabled: false };
    }
    
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored) as LanguagePreferences;
      } catch {
        // Fall through to default
      }
    }
    
    // Default to detected device language
    return {
      language: detectDeviceLanguage(),
      dialect: null,
      dialectEnabled: false,
    };
  });
  
  const [hasCompletedLanguageOnboarding, setHasCompletedLanguageOnboarding] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(LANGUAGE_ONBOARDING_KEY) === "true";
  });
  
  const deviceLanguage = useMemo(() => detectDeviceLanguage(), []);
  
  // Persist preferences
  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);
  
  // Get translations — start with sync (cached/en), then async-load if needed
  const [t, setT] = useState<TranslationStrings>(() => getTranslations(preferences.language));
  
  useEffect(() => {
    // Sync update for cached languages
    setT(getTranslations(preferences.language));
    // Async load if not cached yet
    getTranslationsAsync(preferences.language).then(setT);
  }, [preferences.language]);
  
  // Get text direction
  const dir = useMemo(() => {
    const langInfo = getLanguageInfo(preferences.language);
    return langInfo?.dir || "ltr";
  }, [preferences.language]);
  
  // Update document direction for RTL
  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = preferences.language;
  }, [dir, preferences.language]);
  
  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setPreferences(prev => ({ ...prev, language: lang }));
  }, []);
  
  const setDialect = useCallback((dialect: SupportedDialect | null) => {
    setPreferences(prev => ({ ...prev, dialect }));
  }, []);
  
  const setDialectEnabled = useCallback((enabled: boolean) => {
    setPreferences(prev => ({ ...prev, dialectEnabled: enabled }));
  }, []);
  
  const useDeviceLanguage = useCallback(() => {
    setLanguage(deviceLanguage);
  }, [deviceLanguage, setLanguage]);
  
  const completeLanguageOnboarding = useCallback(() => {
    setHasCompletedLanguageOnboarding(true);
    localStorage.setItem(LANGUAGE_ONBOARDING_KEY, "true");
  }, []);
  
  // Dialect message getters - fall back to English messages if dialect not enabled
  const getMotivation = useCallback(() => {
    if (preferences.dialectEnabled && preferences.dialect) {
      const dialectMsg = getDialectMotivation(preferences.dialect);
      if (dialectMsg) return dialectMsg;
    }
    return "";
  }, [preferences.dialectEnabled, preferences.dialect]);
  
  const getSuccess = useCallback(() => {
    if (preferences.dialectEnabled && preferences.dialect) {
      const dialectMsg = getDialectSuccess(preferences.dialect);
      if (dialectMsg) return dialectMsg;
    }
    return "";
  }, [preferences.dialectEnabled, preferences.dialect]);
  
  const getReminder = useCallback(() => {
    if (preferences.dialectEnabled && preferences.dialect) {
      const dialectMsg = getDialectReminder(preferences.dialect);
      if (dialectMsg) return dialectMsg;
    }
    return "";
  }, [preferences.dialectEnabled, preferences.dialect]);
  
  const getWelcome = useCallback(() => {
    if (preferences.dialectEnabled && preferences.dialect) {
      const dialectMsg = getDialectWelcome(preferences.dialect);
      if (dialectMsg) return dialectMsg;
    }
    return "";
  }, [preferences.dialectEnabled, preferences.dialect]);
  
  const getStreakMessage = useCallback(() => {
    if (preferences.dialectEnabled && preferences.dialect) {
      const dialectMsg = getDialectStreak(preferences.dialect);
      if (dialectMsg) return dialectMsg;
    }
    return "";
  }, [preferences.dialectEnabled, preferences.dialect]);
  
  const value: LanguageContextValue = {
    language: preferences.language,
    dialect: preferences.dialect,
    dialectEnabled: preferences.dialectEnabled,
    t,
    dir,
    setLanguage,
    setDialect,
    setDialectEnabled,
    getMotivation,
    getSuccess,
    getReminder,
    getWelcome,
    getStreakMessage,
    hasCompletedLanguageOnboarding,
    completeLanguageOnboarding,
    deviceLanguage,
    useDeviceLanguage,
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

/**
 * Hook for just translations (simpler API)
 */
export function useTranslations(): TranslationStrings {
  const { t } = useLanguage();
  return t;
}
